'use client';

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { visuallyHidden } from '@mui/utils';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type ExpandedState,
  type VisibilityState,
  type Row,
} from '@tanstack/react-table';

export type SortOrder = 'asc' | 'desc';

export interface DataTableSorting {
  sortBy?: string;
  sortOrder?: SortOrder;
  onSortChange: (sortBy: string, sortOrder: SortOrder) => void;
}

export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  rowsPerPageOptions?: number[];
  labelRowsPerPage?: string;
}

export interface DataTableSelection {
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

export interface DataTablePersistedState {
  columnSizing?: ColumnSizingState;
  columnVisibility?: VisibilityState;
}

export interface DataTableColumnGroup {
  /** Label rendered in the extra grouped-header row. */
  label: ReactNode;
  /** Number of leaf columns (in column definition order) this group spans. */
  span: number;
  align?: 'left' | 'right' | 'center';
}

export interface DataTableHierarchy<T> {
  /** Returns this row's children, or `undefined`/`[]` for leaf rows. */
  getSubRows: (row: T) => T[] | undefined;
  /**
   * Whether rows start expanded. Pass `true` to expand every row, or a
   * predicate to decide per row/depth (e.g. only the top level). Defaults
   * to `false` (all collapsed).
   */
  defaultAllRowsExpanded?: boolean | ((row: T, depth: number) => boolean);
}

export interface DataTableExpandableDetail<T> {
  /** Returns whether a row has expandable detail content. Defaults to true. */
  hasDetail?: (row: T) => boolean;
  /** Renders the full-width detail panel for a row. */
  renderDetail: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  getRowId: (row: T) => string;

  pagination?: DataTablePagination;
  sorting?: DataTableSorting;
  selection?: DataTableSelection;

  enableColumnResizing?: boolean;
  storageKey?: string;

  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  stickyHeader?: boolean;
  density?: 'compact' | 'standard' | 'comfortable';
  ariaLabel?: string;

  /** Enables tree-style rendering with expand/collapse toggles and indentation. */
  hierarchy?: DataTableHierarchy<T>;
  /** Renders an expandable detail row below each row when toggled open. */
  expandableDetail?: DataTableExpandableDetail<T>;
  /** Optional per-row `sx` override, e.g. to highlight synthetic/virtual rows. */
  getRowSx?: (row: T) => Record<string, unknown>;
  /**
   * Renders an extra header row above the normal column headers, with each
   * group spanning N leaf columns (colSpan). Spans must add up to the total
   * number of leaf columns (excluding the selection/row-actions columns).
   */
  columnGroups?: DataTableColumnGroup[];
  /** Arbitrary content rendered inside a `<TableFooter>`, e.g. a totals row. */
  footer?: ReactNode;
}

const MIN_COLUMN_SIZE = 60;
const DEFAULT_COLUMN_SIZE = 150;
const MAX_COLUMN_SIZE = 800;
const LOADING_SKELETON_ROWS = 5;

function loadPersistedState(storageKey?: string): DataTablePersistedState {
  if (!storageKey || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as DataTablePersistedState) : {};
  } catch {
    return {};
  }
}

function savePersistedState(storageKey: string, state: DataTablePersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

/**
 * Headless TanStack Table rendered with MUI primitives.
 *
 * Supports server-controlled sorting, pagination and selection; column
 * resizing with optional localStorage persistence via `storageKey`.
 */
export function DataTable<T>({
  data,
  columns,
  getRowId,
  pagination,
  sorting,
  selection,
  enableColumnResizing = true,
  storageKey,
  onRowClick,
  rowActions,
  loading = false,
  emptyMessage = 'No data',
  stickyHeader = true,
  density = 'standard',
  ariaLabel,
  hierarchy,
  expandableDetail,
  getRowSx,
  columnGroups,
  footer,
}: DataTableProps<T>) {
  const persisted = useMemo(() => loadPersistedState(storageKey), [storageKey]);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    persisted.columnSizing ?? {}
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    persisted.columnVisibility ?? {}
  );
  const [expanded, setExpanded] = useState<ExpandedState>(() =>
    hierarchy?.defaultAllRowsExpanded === true ? true : {}
  );
  const didInitDefaultExpansionRef = useRef(false);

  useEffect(() => {
    const defaultExpanded = hierarchy?.defaultAllRowsExpanded;
    if (typeof defaultExpanded !== 'function' || didInitDefaultExpansionRef.current || data.length === 0) {
      return;
    }
    didInitDefaultExpansionRef.current = true;
    const result: Record<string, boolean> = {};
    const walk = (rows: T[], depth: number) => {
      rows.forEach((row) => {
        if (defaultExpanded(row, depth)) result[getRowId(row)] = true;
        const subRows = hierarchy!.getSubRows(row);
        if (subRows && subRows.length > 0) walk(subRows, depth + 1);
      });
    };
    walk(data, 0);
    setExpanded(result);
  }, [data, hierarchy, getRowId]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!storageKey) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      savePersistedState(storageKey, { columnSizing, columnVisibility });
    }, 300);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [storageKey, columnSizing, columnVisibility]);

  const table = useReactTable<T>({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    ...((hierarchy || expandableDetail) && {
      ...(hierarchy && { getSubRows: (row: T) => hierarchy.getSubRows(row) }),
      getExpandedRowModel: getExpandedRowModel(),
      onExpandedChange: setExpanded,
    }),
    columnResizeMode: 'onChange',
    enableColumnResizing,
    manualSorting: !!sorting,
    manualPagination: !!pagination,
    state: {
      columnSizing,
      columnVisibility,
      ...((hierarchy || expandableDetail) && { expanded }),
    },
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    defaultColumn: {
      minSize: MIN_COLUMN_SIZE,
      size: DEFAULT_COLUMN_SIZE,
      maxSize: MAX_COLUMN_SIZE,
    },
  });

  const leafColumns = table.getVisibleLeafColumns();
  const totalVisibleColumns =
    leafColumns.length + (selection ? 1 : 0) + (expandableDetail ? 1 : 0);

  const currentPageIds = useMemo(
    () => data.map((row) => getRowId(row)),
    [data, getRowId]
  );

  const isAllSelected =
    selection !== undefined &&
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selection.selected.has(id));

  const isIndeterminate =
    selection !== undefined &&
    !isAllSelected &&
    currentPageIds.some((id) => selection.selected.has(id));

  // Latest selection & current-page ids in refs so the toggle/select-all
  // callbacks below can keep stable identity across selection updates. With
  // unstable callbacks the per-row memo below would never hit, and toggling
  // one row would re-render every other row's cells.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const currentPageIdsRef = useRef(currentPageIds);
  currentPageIdsRef.current = currentPageIds;
  const isAllSelectedRef = useRef(isAllSelected);
  isAllSelectedRef.current = isAllSelected;

  const handleSelectAll = useCallback(() => {
    const sel = selectionRef.current;
    if (!sel) return;
    const next = new Set(sel.selected);
    const ids = currentPageIdsRef.current;
    if (isAllSelectedRef.current) {
      ids.forEach((id) => next.delete(id));
    } else {
      ids.forEach((id) => next.add(id));
    }
    sel.onChange(next);
  }, []);

  const handleToggleRow = useCallback((id: string) => {
    const sel = selectionRef.current;
    if (!sel) return;
    const next = new Set(sel.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    sel.onChange(next);
  }, []);

  const handleSort = useCallback(
    (columnId: string) => {
      if (!sorting) return;
      const isCurrent = sorting.sortBy === columnId;
      const nextOrder: SortOrder =
        isCurrent && sorting.sortOrder === 'asc' ? 'desc' : 'asc';
      sorting.onSortChange(columnId, nextOrder);
    },
    [sorting]
  );

  const handleRowClick = useCallback(
    (row: T) => {
      onRowClick?.(row);
    },
    [onRowClick]
  );

  const cellPadding: 'none' | 'normal' = density === 'compact' ? 'none' : 'normal';
  const cellPaddingY = density === 'compact' ? 0.5 : density === 'comfortable' ? 2 : 1;

  /**
   * CSS variables for column widths. Recomputed only when column sizing changes.
   * Applying widths via CSS vars lets the `<table>` element take a single style
   * update during a drag, instead of every cell re-rendering in React.
   */
  const columnSizingState = table.getState().columnSizing;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const isResizingAnyColumn = columnSizingInfo.isResizingColumn !== false;

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const vars: Record<string, string> = {};
    for (const header of headers) {
      vars[`--col-${header.column.id}-size`] = `${header.getSize()}px`;
    }
    return vars;
  }, [table, columnSizingState, columnSizingInfo]);

  const renderHeaderLabel = (
    columnId: string,
    header: ReactNode,
    canSort: boolean
  ): ReactNode => {
    if (!canSort || !sorting) return header;
    const active = sorting.sortBy === columnId;
    return (
      <TableSortLabel
        active={active}
        direction={active ? sorting.sortOrder ?? 'asc' : 'asc'}
        onClick={() => handleSort(columnId)}
        // Inactive sortable columns keep a faint bidirectional icon visible at
        // all times (MUI's default only reveals it on hover), so users can tell
        // sortable columns apart at a glance. The active column uses a single
        // chevron from the same icon family (MUI rotates it per direction) so
        // both states share the same visual weight — MUI's default solid arrow
        // looked jarringly different next to the double chevron.
        IconComponent={active ? KeyboardArrowDownIcon : UnfoldMoreIcon}
        sx={
          active
            ? undefined
            : {
                '& .MuiTableSortLabel-icon': { opacity: 0.35 },
                '&:hover .MuiTableSortLabel-icon': { opacity: 0.7 },
              }
        }
      >
        {header}
        {active && (
          <Box component="span" sx={visuallyHidden}>
            {sorting.sortOrder === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </Box>
        )}
      </TableSortLabel>
    );
  };

  const bodyContent = (
    <DataTableBody
      table={table}
      data={data}
      loading={loading}
      selection={selection}
      leafColumnIds={leafColumns.map((c) => c.id)}
      totalVisibleColumns={totalVisibleColumns + (rowActions ? 1 : 0)}
      emptyMessage={emptyMessage}
      cellPadding={cellPadding}
      cellPaddingY={cellPaddingY}
      enableExpanding={!!hierarchy || !!expandableDetail}
      expandableDetail={expandableDetail}
      getRowSx={getRowSx}
      onRowClick={onRowClick}
      rowActions={rowActions}
      onToggleRow={handleToggleRow}
      onRowActivate={handleRowClick}
      isResizingAnyColumn={isResizingAnyColumn}
    />
  );

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }} elevation={0}>
      <TableContainer>
        <Table
          stickyHeader={stickyHeader}
          aria-label={ariaLabel}
          sx={{ tableLayout: 'fixed' }}
          style={columnSizeVars}
        >
          <TableHead>
            {columnGroups && (
              <TableRow>
                {selection && <TableCell padding="checkbox" />}
                {columnGroups.map((group, index) => (
                  <TableCell
                    key={index}
                    colSpan={group.span}
                    align={group.align ?? 'center'}
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      color: 'text.secondary',
                      bgcolor: 'action.hover',
                    }}
                  >
                    {group.label}
                  </TableCell>
                ))}
                {expandableDetail && <TableCell padding="checkbox" />}
                {rowActions && <TableCell />}
              </TableRow>
            )}
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {selection && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'Select all rows' }}
                    />
                  </TableCell>
                )}
                {expandableDetail && <TableCell padding="checkbox" />}
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const canSort = header.column.columnDef.enableSorting !== false && !!sorting;
                  const canResize = enableColumnResizing && header.column.getCanResize();
                  const align = (header.column.columnDef.meta as { align?: 'left' | 'right' | 'center' } | undefined)?.align ?? 'left';
                  const isResizing = header.column.getIsResizing();
                  return (
                    <TableCell
                      key={header.id}
                      align={align}
                      padding={cellPadding}
                      sx={{
                        width: `var(--col-${header.column.id}-size)`,
                        position: 'relative',
                        userSelect: 'none',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        py: cellPaddingY,
                      }}
                      sortDirection={
                        sorting?.sortBy === columnId ? sorting.sortOrder ?? false : false
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : renderHeaderLabel(
                            columnId,
                            flexRender(header.column.columnDef.header, header.getContext()),
                            canSort
                          )}
                      {canResize && (
                        <Box
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${columnId} column`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: '20%',
                            right: 0,
                            height: '60%',
                            width: '8px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&::after': {
                              content: '""',
                              display: 'block',
                              width: '2px',
                              height: '100%',
                              backgroundColor: isResizing ? 'primary.main' : 'divider',
                              opacity: isResizing ? 1 : 0.6,
                              transition: 'opacity 120ms, background-color 120ms, width 120ms',
                            },
                            '&:hover::after': {
                              width: '3px',
                              opacity: 1,
                              backgroundColor: 'primary.main',
                            },
                          }}
                        />
                      )}
                    </TableCell>
                  );
                })}
                {rowActions && (
                  <TableCell
                    key="actions-header"
                    align="right"
                    padding={cellPadding}
                    sx={{
                      width: '1px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      py: cellPaddingY,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          {bodyContent}
          {footer && <TableFooter>{footer}</TableFooter>}
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_, newPage) => pagination.onPageChange(newPage + 1)}
          onRowsPerPageChange={(event) =>
            pagination.onPageSizeChange?.(parseInt(event.target.value, 10))
          }
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? [10, 20, 50, 100]}
          labelRowsPerPage={pagination.labelRowsPerPage ?? 'Per page:'}
        />
      )}
    </Paper>
  );
}

/**
 * Body renderer split out so we can memoize and skip re-renders during column
 * drag. When `isResizingAnyColumn` is true the memo comparator returns true →
 * React skips the body; column widths update purely via CSS variables on the
 * `<table>` element. As soon as the drag ends, React re-renders once with the
 * new sizes (and all other prop changes like selection/loading flow normally).
 */
interface DataTableBodyProps<T> {
  table: ReturnType<typeof useReactTable<T>>;
  data: T[];
  loading: boolean;
  selection: DataTableSelection | undefined;
  leafColumnIds: string[];
  totalVisibleColumns: number;
  emptyMessage: string;
  cellPadding: 'none' | 'normal';
  cellPaddingY: number;
  enableExpanding: boolean;
  expandableDetail: DataTableExpandableDetail<T> | undefined;
  getRowSx: ((row: T) => Record<string, unknown>) | undefined;
  onRowClick: ((row: T) => void) | undefined;
  rowActions: ((row: T) => ReactNode) | undefined;
  onToggleRow: (id: string) => void;
  onRowActivate: (row: T) => void;
  isResizingAnyColumn: boolean;
}

function DataTableBodyInner<T>({
  table,
  data,
  loading,
  selection,
  leafColumnIds,
  totalVisibleColumns,
  emptyMessage,
  cellPadding,
  cellPaddingY,
  enableExpanding,
  expandableDetail,
  getRowSx,
  onRowClick,
  rowActions,
  onToggleRow,
  onRowActivate,
}: DataTableBodyProps<T>) {
  if (loading) {
    return (
      <TableBody>
        {Array.from({ length: LOADING_SKELETON_ROWS }).map((_, rowIndex) => (
          <TableRow key={`skeleton-${rowIndex}`}>
            {selection && (
              <TableCell padding="checkbox">
                <Skeleton variant="rectangular" width={18} height={18} />
              </TableCell>
            )}
            {expandableDetail && (
              <TableCell padding="checkbox">
                <Skeleton variant="circular" width={20} height={20} />
              </TableCell>
            )}
            {leafColumnIds.map((columnId) => (
              <TableCell
                key={columnId}
                sx={{ width: `var(--col-${columnId}-size)` }}
              >
                <Skeleton variant="text" width="80%" />
              </TableCell>
            ))}
            {rowActions && (
              <TableCell sx={{ width: '1px' }}>
                <Skeleton variant="circular" width={24} height={24} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    );
  }

  if (data.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={totalVisibleColumns} align="center" sx={{ py: 8 }}>
            <Typography variant="body1" color="text.secondary">
              {emptyMessage}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {table.getRowModel().rows.map((row: Row<T>) => {
        const id = row.id;
        const isSelected = selection?.selected.has(id) ?? false;
        // Threading visibleCells through props makes the memo react to column
        // visibility / cell-renderer changes that don't mutate the row ref.
        const visibleCells = row.getVisibleCells();
        return (
          <DataTableRow
            key={id}
            row={row}
            visibleCells={visibleCells}
            isSelected={isSelected}
            selectable={!!selection}
            cellPadding={cellPadding}
            cellPaddingY={cellPaddingY}
            enableExpanding={enableExpanding}
            expandableDetail={expandableDetail}
            isExpanded={row.getIsExpanded()}
            rowSx={getRowSx?.(row.original)}
            onRowClickable={!!onRowClick}
            rowActions={rowActions}
            onToggleRow={onToggleRow}
            onRowActivate={onRowActivate}
          />
        );
      })}
    </TableBody>
  );
}

const DataTableBody = memo(
  DataTableBodyInner,
  (_prev, next) => next.isResizingAnyColumn
) as typeof DataTableBodyInner;

/**
 * Per-row component memoized so toggling selection on one row does not
 * re-run the cell renderers (CopyButton, Chip, Typography, ...) of every
 * other row in the table. Only the row whose `isSelected` actually changed
 * re-renders.
 */
interface DataTableRowProps<T> {
  row: Row<T>;
  visibleCells: ReturnType<Row<T>['getVisibleCells']>;
  isSelected: boolean;
  selectable: boolean;
  cellPadding: 'none' | 'normal';
  cellPaddingY: number;
  enableExpanding: boolean;
  expandableDetail: DataTableExpandableDetail<T> | undefined;
  isExpanded: boolean;
  rowSx: Record<string, unknown> | undefined;
  onRowClickable: boolean;
  rowActions: ((row: T) => ReactNode) | undefined;
  onToggleRow: (id: string) => void;
  onRowActivate: (row: T) => void;
}

function DataTableRowInner<T>({
  row,
  visibleCells,
  isSelected,
  selectable,
  cellPadding,
  cellPaddingY,
  enableExpanding,
  expandableDetail,
  isExpanded,
  rowSx,
  onRowClickable,
  rowActions,
  onToggleRow,
  onRowActivate,
}: DataTableRowProps<T>) {
  const id = row.id;
  const showExpandToggle = !!expandableDetail;
  const hasDetail = expandableDetail?.hasDetail?.(row.original) ?? true;
  const detailColSpan =
    visibleCells.length + (selectable ? 1 : 0) + (showExpandToggle ? 1 : 0) + (rowActions ? 1 : 0);

  return (
    <Fragment>
    <TableRow
      hover
      selected={isSelected}
      onClick={() => onRowActivate(row.original)}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onRowActivate(row.original);
        }
      }}
      sx={{
        cursor: onRowClickable ? 'pointer' : 'default',
        '&:last-child td, &:last-child th': { border: 0 },
        ...rowSx,
      }}
    >
      {selectable && (
        <TableCell
          padding="checkbox"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => onToggleRow(id)}
            inputProps={{ 'aria-label': `Select row ${id}` }}
          />
        </TableCell>
      )}
      {showExpandToggle && (
        <TableCell
          padding="checkbox"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {hasDetail && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              sx={{ p: 0.25 }}
            >
              {isExpanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>
      )}
      {visibleCells.map((cell, cellIndex) => {
        const align =
          (cell.column.columnDef.meta as { align?: 'left' | 'right' | 'center' } | undefined)
            ?.align ?? 'left';
        const isFirstCell = cellIndex === 0;
        return (
          <TableCell
            key={cell.id}
            align={align}
            padding={cellPadding}
            sx={{
              width: `var(--col-${cell.column.id}-size)`,
              maxWidth: `var(--col-${cell.column.id}-size)`,
              overflow: 'hidden',
              py: cellPaddingY,
            }}
          >
            {isFirstCell && enableExpanding && !expandableDetail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', pl: row.depth * 2.5 }}>
                {row.getCanExpand() ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      row.toggleExpanded();
                    }}
                    aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                    sx={{ mr: 0.5, p: 0.25, flexShrink: 0 }}
                  >
                    {row.getIsExpanded() ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 28, flexShrink: 0 }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              </Box>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        );
      })}
      {rowActions && (
        <TableCell
          align="right"
          padding={cellPadding}
          sx={{ width: '1px', py: cellPaddingY }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {rowActions(row.original)}
        </TableCell>
      )}
    </TableRow>
      {showExpandToggle && hasDetail && isExpanded && (
        <TableRow>
          <TableCell colSpan={detailColSpan} sx={{ py: 0, borderBottom: 'none' }}>
            {expandableDetail!.renderDetail(row.original)}
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

const DataTableRow = memo(DataTableRowInner) as typeof DataTableRowInner;

export default DataTable;
